import React from "react";
import { Icon } from "./Icon";
import { Link } from "react-router-dom";
import { MarketingHeader } from "./MarketingHeader";
import { PageMeta } from "./PageMeta";

// FAQ is a standalone route — no parent passes onLogin. Use the same one-line
// flow as App.tsx so the CTA goes straight to Twitch OAuth.
const handleLogin = () => {
  window.location.href = "/api/auth/twitch";
};

/**
 * FAQ page at /faq. Each Q/A pair is emitted twice:
 *   1. Visibly, as accessible <details>/<summary> blocks.
 *   2. As a JSON-LD `FAQPage` structured-data graph — Google can use this to
 *      render expandable FAQ rich snippets directly in search results.
 *
 * Add new questions by editing the FAQS array; both views stay in sync.
 */

interface QA {
  q: string;
  a: string;
}

// Plain-text answers — the JSON-LD `acceptedAnswer.text` is required to be
// HTML-free string by Google's spec. Don't put links inside.
const FAQS: QA[] = [
  {
    q: "What is HAL 8580?",
    a: "HAL 8580 is an AI-powered chat bot for Twitch streamers. It joins your channel, takes on a personality you choose, remembers your regular viewers, reacts to events in real time, and replies in chat. Think of it as a co-host that's always on, never gets tired, and stays in character.",
  },
  {
    q: "Why HAL 8580?",
    a: "It's the prototype of its older sibling HAL 9000, fitted with a C64 SID 8580 sound chip for music and speech synthesis. The name is a little love letter to early-80s computer hardware and the science fiction that grew up with it.",
  },
  {
    q: "How does HAL remember viewers?",
    a: "Every interaction is stored as a vector embedding and indexed per-user. Over time HAL builds a memory of who each viewer is, what they care about, and how they like to engage. It also detects sentiment and trends across conversations. Memory is per-channel and never shared between streamers.",
  },
  {
    q: "Do I need to give HAL mod permissions?",
    a: "HAL works with read-only access in most cases. Optional mod permissions unlock additional capabilities like automated moderation responses and richer follower data, but the core experience works without them.",
  },
  {
    q: "Can I customize HAL's personality?",
    a: "Yes. Start from a preset (DJ, Musician, Gamer, Tech Expert, and more) or write your own personality from scratch. Adjust tone, response frequency, what triggers it, and what topics it stays away from. Personality is locked in for the session so HAL never breaks character mid-stream.",
  },
  {
    q: "Will HAL spam my chat?",
    a: "No. Response frequency is rate-limited by default and configurable per channel. HAL only responds when it's directly addressed, when a trigger matches, or when it judges that a comment is worth engaging with. You can tune this dial up or down at any time.",
  },
  {
    q: "What data does HAL store about my viewers?",
    a: "Just enough to keep the conversation coherent: Twitch ID, username, the messages they send in your channel, and the embeddings derived from those messages for memory lookup. No payment data, no off-platform tracking, no selling of viewer information to third parties.",
  },
  {
    q: "How is HAL different from Nightbot or StreamElements?",
    a: "Nightbot and StreamElements are command-based. They run scripts and respond to !commands. HAL is conversational. It actually understands what's being said, holds a personality, remembers people across sessions, and reacts to live context like current song or game. You can run HAL alongside other bots — they don't conflict.",
  },
  {
    q: "Does HAL work for non-Twitch platforms?",
    a: "Today, Twitch only. Expanding to other platforms (YouTube Live, Kick) is on the roadmap but not committed to a date yet.",
  },
  {
    q: "How do I get started?",
    a: "Log in with Twitch from the landing page. The OAuth flow asks for the minimum scopes HAL needs. Once you're in, pick a personality and HAL joins your chat. The whole flow takes about two minutes.",
  },
];

const PURPLE = "#9146FF";
const CYAN = "#00D4FF";
const BG = "#0a0a0a";
const TEXT = "#e2e8f0";
const MUTED = "#a0aec0";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a,
    },
  })),
};

export const FAQ: React.FC = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 860);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div
      style={{
        backgroundColor: BG,
        color: TEXT,
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <PageMeta
        title="HAL 8580 FAQ — Frequently Asked Questions"
        description="Answers about HAL 8580 — what it does, how memory works, what data it stores, how it differs from Nightbot and StreamElements, and how to get started on Twitch."
        path="/faq"
        breadcrumbs={[{ name: "FAQ" }]}
        jsonLd={jsonLd}
      />

      <MarketingHeader onLogin={handleLogin} />

      <section
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: isMobile ? "32px 20px 64px" : "48px 60px 96px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: isMobile ? "40px" : "56px",
              fontWeight: 700,
              margin: "0 0 16px 0",
              letterSpacing: "-1.5px",
              background: `linear-gradient(135deg, ${PURPLE} 0%, ${CYAN} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Frequently asked.
          </h1>
          <p
            style={{
              color: MUTED,
              fontSize: "18px",
              margin: "0 0 48px 0",
              fontWeight: 300,
              lineHeight: 1.5,
            }}
          >
            The honest answers. If something’s missing, ask us — we’ll add it.
          </p>

          <div style={{ display: "grid", gap: "16px" }}>
            {FAQS.map((f) => (
              <details
                key={f.q}
                style={{
                  padding: "20px 24px",
                  borderRadius: "12px",
                  background: "rgba(145, 70, 255, 0.04)",
                  border: "1px solid rgba(145, 70, 255, 0.12)",
                }}
              >
                <summary
                  style={{
                    fontSize: isMobile ? "16px" : "18px",
                    fontWeight: 600,
                    color: TEXT,
                    cursor: "pointer",
                    listStyle: "none",
                    outline: "none",
                  }}
                >
                  {f.q}
                </summary>
                <p
                  style={{
                    color: "#cbd5e0",
                    marginTop: "16px",
                    marginBottom: 0,
                    fontSize: "15px",
                    lineHeight: 1.7,
                    fontWeight: 300,
                  }}
                >
                  {f.a}
                </p>
              </details>
            ))}
          </div>

          <p
            style={{
              color: MUTED,
              marginTop: "48px",
              fontSize: "15px",
              fontWeight: 300,
            }}
          >
            Ready to try it?{" "}
            <Link to="/login" style={{ color: PURPLE, fontWeight: 500 }}>
              Log in with Twitch{" "}
              <Icon
                name="arrow-right"
                size={14}
                style={{ verticalAlign: "-2px", marginLeft: "6px" }}
              />
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};
